import type { ReactNode } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { Objective } from "@/types/entities";

interface ObjectiveSummaryStripProps {
  objectives: Objective[];
}

export interface ObjectiveSummaryStats {
  count: number;
  measuredCount: number;
  avgProgress: number;
  onTrack: number;
  atRisk: number;
  offTrack: number;
}

/**
 * Pure derive: bucket objectives by completion_percent.
 *
 * Rules:
 *  - Objectives with null completion_percent (zero KRs) are excluded from the
 *    average AND from on/at-risk/off-track buckets.
 *  - Empty lists render zeros (no divide-by-zero).
 *  - Buckets: >=50 on track, 25-<50 at risk, <25 off track.
 */
export function computeSummaryStats(objectives: Objective[]): ObjectiveSummaryStats {
  let measured = 0;
  let sum = 0;
  let onTrack = 0;
  let atRisk = 0;
  let offTrack = 0;

  for (const obj of objectives) {
    const pct = obj.completion_percent;
    if (pct == null) continue;
    measured += 1;
    sum += pct;
    if (pct >= 50) onTrack += 1;
    else if (pct >= 25) atRisk += 1;
    else offTrack += 1;
  }

  return {
    count: objectives.length,
    measuredCount: measured,
    avgProgress: measured === 0 ? 0 : Math.round(sum / measured),
    onTrack,
    atRisk,
    offTrack,
  };
}

interface SummaryCardProps {
  label: string;
  value: ReactNode;
  meta: ReactNode;
}

function SummaryCard({ label, value, meta }: SummaryCardProps) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-1.5 p-4">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
          {label}
        </span>
        <span className="font-heading text-2xl font-semibold leading-none tracking-tight text-[hsl(var(--foreground))]">
          {value}
        </span>
        <span className="flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))]">
          {meta}
        </span>
      </CardContent>
    </Card>
  );
}

function Dot({ className }: { className: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn("inline-block h-1.5 w-1.5 rounded-full", className)}
    />
  );
}

export function ObjectiveSummaryStrip({ objectives }: ObjectiveSummaryStripProps) {
  const stats = computeSummaryStats(objectives);
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <SummaryCard
        label="Avg Progress"
        value={
          <>
            {stats.avgProgress}
            <span className="ml-0.5 text-base font-normal text-[hsl(var(--muted-foreground))]">
              %
            </span>
          </>
        }
        meta={`across ${stats.count} ${stats.count === 1 ? "objective" : "objectives"}`}
      />
      <SummaryCard
        label="On Track"
        value={stats.onTrack}
        meta={
          <>
            <Dot className="bg-[hsl(var(--info))]" />
            healthy pace
          </>
        }
      />
      <SummaryCard
        label="At Risk"
        value={stats.atRisk}
        meta={
          <>
            <Dot className="bg-[hsl(var(--accent))]" />
            needs attention
          </>
        }
      />
      <SummaryCard
        label="Off Track"
        value={stats.offTrack}
        meta={
          <>
            <Dot className="bg-[hsl(var(--destructive))]" />
            behind plan
          </>
        }
      />
    </div>
  );
}
